import { FC, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { PasswordRequirements } from '@components/molecules/PasswordRequirements';
import { useTranslation } from '@hooks/useTranslation';
import { buildPasswordSchema } from '@utils/passwordPolicy';

export interface SetupFormProps {
  /** Verified invitee address, shown read-only. */
  email: string;
  /** Resolves when the request settles; the form disables itself while it runs. */
  onSubmit: (password: string) => Promise<void> | void;
  isSubmitting: boolean;
  /** Cognito's InvalidPasswordException text, verbatim (not translated). Cleared on next edit. */
  serverPolicyMessage?: string | null;
  /** Inline sentence for a network/server failure on Save; the typed password is kept. */
  submitError?: string | null;
  onPasswordEdited?: () => void;
}

/**
 * Sprint 47, story-232/233. Email read-only, new password, confirm, the shared checklist (always
 * visible here) and Save. Save is enabled once both fields are non-empty so a failed validation
 * can show its text on press; it is disabled while saving. The password goes to `onSubmit`
 * exactly as typed (no trim, no escaping).
 */
export const SetupForm: FC<SetupFormProps> = ({
  email,
  onSubmit,
  isSubmitting,
  serverPolicyMessage,
  submitError,
  onPasswordEdited,
}) => {
  const tSetup = useTranslation('setup');
  const tPolicy = useTranslation('passwordPolicy');

  const validationSchema = useMemo(
    () =>
      Yup.object({
        password: buildPasswordSchema(tPolicy),
        confirmPassword: Yup.string()
          .required(tPolicy.confirmRequired)
          .oneOf([Yup.ref('password')], tPolicy.mismatch),
      }),
    [tPolicy],
  );

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema,
    validateOnMount: false,
    onSubmit: async (values) => {
      await onSubmit(values.password);
    },
  });

  const passwordError =
    serverPolicyMessage ||
    (formik.touched.password ? formik.errors.password : undefined);
  const confirmError = formik.touched.confirmPassword
    ? formik.errors.confirmPassword
    : undefined;
  const canSave =
    formik.values.password.length > 0 &&
    formik.values.confirmPassword.length > 0 &&
    !isSubmitting;

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="flex flex-col gap-4">
      <FormGroup label={tSetup.emailLabel} htmlFor="setup-email">
        <Input
          id="setup-email"
          name="email"
          type="email"
          value={email}
          readOnly
          disabled
          autoComplete="username"
        />
      </FormGroup>
      <FormGroup
        label={tSetup.passwordLabel}
        htmlFor="setup-password"
        error={passwordError}>
        <Input
          id="setup-password"
          name="password"
          type="password"
          autoComplete="new-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={formik.values.password}
          onChange={(event) => {
            onPasswordEdited?.();
            formik.handleChange(event);
          }}
          onBlur={formik.handleBlur}
          disabled={isSubmitting}
          aria-invalid={Boolean(passwordError)}
          aria-describedby={passwordError ? 'setup-password-error' : undefined}
          error={Boolean(passwordError)}
        />
      </FormGroup>
      <PasswordRequirements value={formik.values.password} visible />
      <FormGroup
        label={tSetup.confirmLabel}
        htmlFor="setup-confirm-password"
        error={confirmError}>
        <Input
          id="setup-confirm-password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isSubmitting}
          aria-invalid={Boolean(confirmError)}
          error={Boolean(confirmError)}
        />
      </FormGroup>
      {submitError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {submitError}
        </p>
      )}
      <Button
        fullWidth
        type="submit"
        variant="primary"
        loading={isSubmitting}
        disabled={!canSave}>
        {isSubmitting ? tSetup.saving : tSetup.save}
      </Button>
    </form>
  );
};

export default SetupForm;
